import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Sparkles, Users } from 'lucide-react';
import { supabase } from '../supabaseClient';
import FollowButton from '../components/FollowButton';
import { useFollows } from '../contexts/FollowsContext';

function formatStatus(status) {
  const normalized = String(status || '').replaceAll('_', ' ').trim();
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function AvatarBadge({ user }) {
  const fallback = user?.username?.[0] || '?';

  return (
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-3xl font-black shadow-lg">
      {fallback.toUpperCase()}
    </div>
  );
}

function TrialListItem({ trial }) {
  const createdAtLabel = formatDate(trial.created_at);
  const totalVotes = Number(trial.total_votes || 0);

  return (
    <Link
      to={`/t/${trial.id}`}
      className="group block rounded-2xl border border-slate-700 bg-slate-800/80 p-4 hover:border-slate-600 hover:bg-slate-800 transition"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center text-2xl leading-none shrink-0">
          {trial.image_emoji || '⚖️'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-base sm:text-lg font-black text-slate-50 line-clamp-2 group-hover:text-white">
              {trial.title}
            </div>
            <span className="rounded-full border border-slate-600 bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300">
              {formatStatus(trial.status)}
            </span>
          </div>
          <div className="mt-1 text-sm text-slate-300 line-clamp-1">
            {trial.side_a} vs {trial.side_b}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
            <span className="rounded-full bg-slate-900 px-2.5 py-1 border border-slate-700">
              {totalVotes} vote{totalVotes === 1 ? '' : 's'}
            </span>
            {createdAtLabel ? (
              <span className="rounded-full bg-slate-900 px-2.5 py-1 border border-slate-700">
                Created {createdAtLabel}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function PublicUserProfile() {
  const { username } = useParams();
  const { currentUserId } = useFollows();
  const [profile, setProfile] = useState(null);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizedUsername = useMemo(() => String(username || '').trim(), [username]);
  const isOwnProfile = Boolean(currentUserId && profile?.id && String(currentUserId) === String(profile.id));
  const totalCreatedTrials = trials.length;
  const completedTrialCount = useMemo(
    () => trials.filter((trial) => ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(String(trial.status || '').toLowerCase())).length,
    [trials]
  );
  const completedTrials = useMemo(
    () => trials.filter((trial) => ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(String(trial.status || '').toLowerCase())),
    [trials]
  );
  const totalVotesReceived = useMemo(
    () => trials.reduce((sum, trial) => sum + Number(trial.total_votes || 0), 0),
    [trials]
  );
  const avgVotesPerCompletedTrial = useMemo(
    () => (
      completedTrials.length > 0
        ? Math.round(completedTrials.reduce((sum, trial) => sum + Number(trial.total_votes || 0), 0) / completedTrials.length)
        : 0
    ),
    [completedTrials]
  );
  const recentVerdicts = useMemo(() => completedTrials.slice(0, 6), [completedTrials]);

  useEffect(() => {
    let alive = true;

    async function loadProfile() {
      if (!normalizedUsername) {
        if (alive) {
          setError('User not found.');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: userRow, error: userError } = await supabase
          .from('users')
          .select('id, username, followers_count, following_count')
          .eq('username', normalizedUsername)
          .maybeSingle();

        if (userError) throw userError;
        if (!userRow) {
          if (alive) {
            setProfile(null);
            setTrials([]);
            setError('User not found.');
            setLoading(false);
          }
          return;
        }

        const [{ count: followersCount }, { count: followingCount }, { data: trialsData, error: trialsError }] = await Promise.all([
          supabase.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', userRow.id),
          supabase.from('follows').select('*', { count: 'exact', head: true }).eq('follower_id', userRow.id),
          supabase
            .from('trials')
            .select('id, title, side_a, side_b, image_emoji, status, total_votes, created_at')
            .or(`created_by.eq.${userRow.id},creator_id.eq.${userRow.id}`)
            .order('created_at', { ascending: false }),
        ]);

        if (trialsError) throw trialsError;

        if (!alive) return;

        setProfile({
          ...userRow,
          followers_count: followersCount ?? userRow.followers_count ?? 0,
          following_count: followingCount ?? userRow.following_count ?? 0,
        });
        setTrials(trialsData || []);
      } catch (err) {
        if (!alive) return;
        console.error('[public_profile] load_failed', {
          username: normalizedUsername,
          message: err?.message || String(err),
        });
        setProfile(null);
        setTrials([]);
        setError('Could not load this profile.');
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadProfile();
    return () => { alive = false; };
  }, [normalizedUsername]);

  useEffect(() => {
    if (!profile?.id || !normalizedUsername) return;
    console.log('[public_profile_viewed]', {
      profile_user_id: profile.id,
      username: normalizedUsername,
      created_trials: trials.length,
    });
    try {
      window.trackEvent?.('public_profile_viewed', {
        profile_user_id: profile.id,
        username: normalizedUsername,
        created_trials: trials.length,
      });
    } catch {}
  }, [profile?.id, normalizedUsername, trials.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading profile...</span>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="mt-4 rounded-2xl border border-slate-700 bg-slate-800/80 p-6">
            <div className="text-xl font-black">{error || 'User not found.'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="mt-4 rounded-3xl border border-slate-700 bg-slate-800/80 p-6 sm:p-7 overflow-hidden relative">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-purple-600/10 via-pink-500/10 to-sky-500/10 pointer-events-none" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <AvatarBadge user={profile} />
              <div className="min-w-0">
                <div className="text-2xl font-black text-slate-50 break-words leading-tight">
                  {profile.username}
                </div>
                <div className="mt-1 text-sm text-slate-400 break-all">@{profile.username}</div>
                <div className="mt-2 text-sm text-slate-300">
                  Created {totalCreatedTrials} trial{totalCreatedTrials === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            {!isOwnProfile ? (
              <div className="self-start sm:self-auto">
                <FollowButton targetUserId={profile.id} targetUsername={profile.username} />
              </div>
            ) : null}
          </div>

          <div className="relative mt-6 grid grid-cols-3 gap-3">
            <div className="rounded-2xl bg-slate-900 p-4 border border-slate-700">
              <div className="text-xs uppercase tracking-wide text-slate-400">Followers</div>
              <div className="mt-1 text-2xl font-black text-slate-50">{Number(profile.followers_count || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-2xl bg-slate-900 p-4 border border-slate-700">
              <div className="text-xs uppercase tracking-wide text-slate-400">Following</div>
              <div className="mt-1 text-2xl font-black text-slate-50">{Number(profile.following_count || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-2xl bg-slate-900 p-4 border border-slate-700">
              <div className="text-xs uppercase tracking-wide text-slate-400">Created Trials</div>
              <div className="mt-1 text-2xl font-black text-slate-50">{totalCreatedTrials.toLocaleString()}</div>
            </div>
          </div>

          <div className="relative mt-4 rounded-2xl border border-slate-700 bg-slate-900/70 p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-300">
              <span>
                <span className="font-black text-slate-50">{completedTrialCount}</span>{' '}
                verdict{completedTrialCount === 1 ? '' : 's'} delivered
              </span>
              <span className="text-slate-600">•</span>
              <span>
                <span className="font-black text-slate-50">{totalVotesReceived.toLocaleString()}</span>{' '}
                total votes on their trials
              </span>
              <span className="text-slate-600">•</span>
              <span>
                <span className="font-black text-slate-50">{avgVotesPerCompletedTrial}</span>{' '}
                avg votes per completed trial
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-2 text-slate-200 mb-3">
            <Users className="w-4 h-4" />
            <h2 className="text-lg font-black">Created Trials</h2>
          </div>

          {trials.length === 0 ? (
            <div className="rounded-3xl border border-slate-700 bg-slate-800/80 p-8 text-center">
              <div className="text-4xl mb-3">⚖️</div>
              <div className="text-lg font-black text-slate-100">No public trials yet</div>
              <div className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
                Follow {profile.username} to catch the next trial they create.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {trials.map((trial) => (
                <TrialListItem key={trial.id} trial={trial} />
              ))}
            </div>
          )}
        </div>

        {recentVerdicts.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 text-slate-200 mb-3">
              <Sparkles className="w-4 h-4" />
              <h2 className="text-lg font-black">Recent Verdicts</h2>
            </div>
            <div className="space-y-3">
              {recentVerdicts.map((trial) => (
                <TrialListItem key={`verdict-${trial.id}`} trial={trial} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
